// BM-H398 - s6_legacies. `specific` drives C-10, `pecuniary` C-11, `charity`
// C-12, `age_contingent.enabled` C-13. The charity list is also mapped (in
// willsPayload.normalisePayload) to the recipient shape the engine's charity
// warnings read.

import { MoneyInput, NumberInput, QuestionRow, RepeatList, SectionIntro, Select, TextInput, YesNo, sectionProps, replaceAt, removeAt } from "./fields";
import PersonPicker from "./PersonPicker";
import { CHARITY_CONTROLLED_BASES, CHARITY_CONTROLLED_TYPES, CHARITY_IDENTIFIER_TYPES, CHARITY_JURISDICTIONS, newId } from "./willsPayload";
import { CallbackHint, UnsupportedNotice } from "./removedOptions";

// BM-H3636: "To a substitute I name" is no longer offered (solicitor ruling, 5 Oct 2026). A saved choice is kept
// and flagged by UnsupportedNotice.
export const SUBSTITUTION_OPTIONS = [
  { value: "to_their_children", label: "To their children" },
  { value: "to_residue", label: "Back into the rest of my estate" },
];

const CHARITY_GIFT_TYPE = [
  { value: "pecuniary", label: "A sum of money" },
  { value: "residue_percent", label: "A percentage of my estate" },
  { value: "specific_item", label: "A specific item" },
];

// BM-H3932 (solicitor's answer 9, 7 Oct 2026): "To a substitute charity I name" is no longer offered. A saved choice is
// kept and flagged by UnsupportedNotice (removedOptions.jsx), and the Will assembler refuses it by name.
export const CHARITY_CEASES = [
  { value: "similar_charity_by_trustees", label: "To a similar charity chosen by my trustees" },
  { value: "to_residue", label: "Back into the rest of my estate" },
];

export default function S6Legacies({ section, setSection, payload, update, person, answers }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const specific = s.specific || [];
  const pecuniary = s.pecuniary || [];
  const charity = s.charity || [];
  const age = s.age_contingent || {};

  return (
    <div data-testid="section-s6_legacies">
      <UnsupportedNotice answers={answers || payload.submission.answers} section="s6_legacies" />
      <SectionIntro title="Specific gifts">
        Individual items or sums of money to specific people or charities. Leave a list empty if it does not apply to you.
      </SectionIntro>
      <CallbackHint testid="s6-substitute-hint">If someone you leave a gift to dies before you, it can go to their children or back into the rest of your estate.</CallbackHint>

      <QuestionRow qid="s6_legacies.specific" label="Specific items to individuals">
        <RepeatList
          testid="s6-specific"
          items={specific}
          onAdd={() => set({ specific: [...specific, { id: newId("gift"), beneficiary_person_id: null, description: "", substitution: null }] })}
          onRemove={(i) => set({ specific: removeAt(specific, i) })}
          addLabel="Add an item"
          render={(g, i) => {
            const edit = (patch) => set({ specific: replaceAt(specific, i, { ...g, ...patch }) });
            return (
              <>
                <PersonPicker payload={payload} update={update} qid="s6_legacies.specific.beneficiary_person_id" label="Who receives it?" value={g.beneficiary_person_id} exclude={[person?.id]} onChange={(pid) => edit({ beneficiary_person_id: pid })} testid={`s6-specific-${i}-beneficiary`} />
                <QuestionRow qid="s6_legacies.specific.description" label="What is the gift?" testid={`s6-specific-${i}-q-description`}>
                  <TextInput testid={`s6-specific-${i}-description`} value={g.description} onChange={(v) => edit({ description: v })} />
                </QuestionRow>
                <QuestionRow qid="s6_legacies.specific.substitution" label="If they die before you, where should it go?" testid={`s6-specific-${i}-q-substitution`}>
                  <Select testid={`s6-specific-${i}-substitution`} value={g.substitution} options={SUBSTITUTION_OPTIONS} onChange={(v) => edit({ substitution: v })} />
                </QuestionRow>
              </>
            );
          }}
        />
      </QuestionRow>

      <QuestionRow qid="s6_legacies.pecuniary" label="Sums of money to individuals">
        <RepeatList
          testid="s6-pecuniary"
          items={pecuniary}
          onAdd={() => set({ pecuniary: [...pecuniary, { id: newId("gift"), beneficiary_person_id: null, amount_gbp: null, substitution: null }] })}
          onRemove={(i) => set({ pecuniary: removeAt(pecuniary, i) })}
          addLabel="Add a sum"
          render={(g, i) => {
            const edit = (patch) => set({ pecuniary: replaceAt(pecuniary, i, { ...g, ...patch }) });
            return (
              <>
                <PersonPicker payload={payload} update={update} qid="s6_legacies.pecuniary.beneficiary_person_id" label="Who receives it?" value={g.beneficiary_person_id} exclude={[person?.id]} onChange={(pid) => edit({ beneficiary_person_id: pid })} testid={`s6-pecuniary-${i}-beneficiary`} />
                <QuestionRow qid="s6_legacies.pecuniary.amount_gbp" label="How much?" testid={`s6-pecuniary-${i}-q-amount`}>
                  <MoneyInput testid={`s6-pecuniary-${i}-amount`} value={g.amount_gbp} onChange={(v) => edit({ amount_gbp: v })} />
                </QuestionRow>
                <QuestionRow qid="s6_legacies.pecuniary.substitution" label="If they die before you, where should it go?" testid={`s6-pecuniary-${i}-q-substitution`}>
                  <Select testid={`s6-pecuniary-${i}-substitution`} value={g.substitution} options={SUBSTITUTION_OPTIONS} onChange={(v) => edit({ substitution: v })} />
                </QuestionRow>
              </>
            );
          }}
        />
      </QuestionRow>

      <CallbackHint testid="s6-charity-ceases-hint">If a charity you leave a gift to no longer exists when you die, the gift can go to a similar charity chosen by your trustees or back into the rest of your estate.</CallbackHint>
      <QuestionRow qid="s6_legacies.charity" label="Gifts to charities">
        <RepeatList
          testid="s6-charity"
          items={charity}
          onAdd={() => set({ charity: [...charity, { id: newId("chy"), charity_name: "", jurisdiction: null, identifier_type: null, registered_number: "", identifier_basis: null, identifier_basis_reference: "", gift_type: null, amount_gbp: null, residue_percent: null, item_description: "", purpose: "", if_charity_ceases: null }] })}
          onRemove={(i) => set({ charity: removeAt(charity, i) })}
          addLabel="Add a charity"
          render={(g, i) => {
            const edit = (patch) => set({ charity: replaceAt(charity, i, { ...g, ...patch }) });
            return (
              <>
                <QuestionRow qid="s6_legacies.charity.charity_name" label="Charity's full registered name" testid={`s6-charity-${i}-q-name`}>
                  <TextInput testid={`s6-charity-${i}-name`} value={g.charity_name} onChange={(v) => edit({ charity_name: v })} />
                </QuestionRow>
                <QuestionRow qid="s6_legacies.charity.jurisdiction" label="Where is the charity based?" testid={`s6-charity-${i}-q-jurisdiction`}>
                  <Select testid={`s6-charity-${i}-jurisdiction`} value={g.jurisdiction} options={CHARITY_JURISDICTIONS} onChange={(v) => edit({ jurisdiction: v })} />
                </QuestionRow>
                {/* BM-H3898: changing the kind of identifier clears the answers that belong to the other kind, so a
                    saved gift never holds both a register number and an exempt or excepted basis. */}
                <QuestionRow qid="s6_legacies.charity.identifier_type" label="How is the charity identified?" testid={`s6-charity-${i}-q-idtype`}>
                  <Select
                    testid={`s6-charity-${i}-idtype`}
                    value={g.identifier_type}
                    options={CHARITY_IDENTIFIER_TYPES}
                    onChange={(v) => edit(CHARITY_CONTROLLED_TYPES.includes(v)
                      ? { identifier_type: v, registered_number: "" }
                      : { identifier_type: v, identifier_basis: null, identifier_basis_reference: "" })}
                  />
                </QuestionRow>
                {g.identifier_type && !CHARITY_CONTROLLED_TYPES.includes(g.identifier_type) && (
                  <QuestionRow qid="s6_legacies.charity.registered_number" label="Charity number" evidence testid={`s6-charity-${i}-q-number`}>
                    <TextInput testid={`s6-charity-${i}-number`} value={g.registered_number} maxLength={24} onChange={(v) => edit({ registered_number: v })} />
                  </QuestionRow>
                )}
                {CHARITY_CONTROLLED_TYPES.includes(g.identifier_type) && (
                  <>
                    <QuestionRow qid="s6_legacies.charity.identifier_basis" label="What identifies this charity?" evidence testid={`s6-charity-${i}-q-basis`}>
                      <Select testid={`s6-charity-${i}-basis`} value={g.identifier_basis} options={CHARITY_CONTROLLED_BASES} onChange={(v) => edit({ identifier_basis: v })} />
                    </QuestionRow>
                    <QuestionRow qid="s6_legacies.charity.identifier_basis_reference" label="The reference for that" testid={`s6-charity-${i}-q-basis-ref`}>
                      <TextInput testid={`s6-charity-${i}-basis-ref`} value={g.identifier_basis_reference} maxLength={80} onChange={(v) => edit({ identifier_basis_reference: v })} />
                    </QuestionRow>
                  </>
                )}
                <QuestionRow qid="s6_legacies.charity.gift_type" label="What is the gift?" testid={`s6-charity-${i}-q-type`}>
                  <Select testid={`s6-charity-${i}-type`} value={g.gift_type} options={CHARITY_GIFT_TYPE} onChange={(v) => edit({ gift_type: v })} />
                </QuestionRow>
                {g.gift_type === "pecuniary" && (
                  <QuestionRow qid="s6_legacies.charity.amount_gbp" label="How much?" testid={`s6-charity-${i}-q-amount`}>
                    <MoneyInput testid={`s6-charity-${i}-amount`} value={g.amount_gbp} onChange={(v) => edit({ amount_gbp: v })} />
                  </QuestionRow>
                )}
                {g.gift_type === "residue_percent" && (
                  <QuestionRow qid="s6_legacies.charity.residue_percent" label="What percentage?" testid={`s6-charity-${i}-q-percent`}>
                    <NumberInput testid={`s6-charity-${i}-percent`} value={g.residue_percent} onChange={(v) => edit({ residue_percent: v })} />
                  </QuestionRow>
                )}
                {g.gift_type === "specific_item" && (
                  <QuestionRow qid="s6_legacies.charity.item_description" label="Which item?" testid={`s6-charity-${i}-q-item`}>
                    <TextInput testid={`s6-charity-${i}-item`} value={g.item_description} onChange={(v) => edit({ item_description: v })} />
                  </QuestionRow>
                )}
                <QuestionRow qid="s6_legacies.charity.purpose" label="Is the gift for a specific purpose within the charity's work?" testid={`s6-charity-${i}-q-purpose`}>
                  <TextInput testid={`s6-charity-${i}-purpose`} value={g.purpose} onChange={(v) => edit({ purpose: v })} />
                </QuestionRow>
                <QuestionRow qid="s6_legacies.charity.if_charity_ceases" label="If the charity no longer exists when you die" testid={`s6-charity-${i}-q-ceases`}>
                  <Select testid={`s6-charity-${i}-ceases`} value={g.if_charity_ceases} options={CHARITY_CEASES} onChange={(v) => edit({ if_charity_ceases: v })} />
                </QuestionRow>
              </>
            );
          }}
        />
      </QuestionRow>

      <QuestionRow qid="s6_legacies.age_contingent.enabled" label="Should gifts to younger beneficiaries be held until they reach a set age?" notKnown={nk("age_contingent")} onNotKnown={(on) => setNk("age_contingent", on)}>
        <YesNo testid="s6-age-contingent" value={age.enabled ?? null} disabled={nk("age_contingent")} onChange={(v) => set({ age_contingent: { ...age, enabled: v } })} />
      </QuestionRow>
      {age.enabled === true && (
        <QuestionRow qid="s6_legacies.age_contingent.vesting_age" label="At what age?">
          <NumberInput testid="s6-vesting-age" min="18" value={age.vesting_age} onChange={(v) => set({ age_contingent: { ...age, vesting_age: v } })} />
        </QuestionRow>
      )}
    </div>
  );
}
